/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive single-file section component — see project guidelines. */
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PARTNERS, STATS_FALLBACK } from "@/data/site";
import { getStats } from "@/lib/api";
import { Play, ExternalLink, Briefcase } from "lucide-react";
import { LazyYouTube } from "./LazyYouTube";

const PLAYLIST_ID = "PL-t8V4eW2g_sipWLIkVgEQfTc4c08cVMV";

// Real Everyday Creative Internship Program episodes from @EDUtainmentPros
const INTERVIEWS = [
    {
        id: "UJZw-seN5Bg",
        title: "Young Mogul Larry",
        guest: "Intern Spotlight",
    },
    {
        id: "SfzkgRkmhDo",
        title: "Let's Go Rehearsal Studios",
        guest: "CEO Jesse",
    },
    {
        id: "gyfV1T_glDM",
        title: "Young Mogul Davonta",
        guest: "Intern Spotlight",
    },
    {
        id: "6qE1rJANz6s",
        title: "Flores Podcast Productions",
        guest: "CEO Johnny",
    },
];

const Counter = ({ value, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    const [n, setN] = useState(0);

    // Count-up animation. `duration`, `start`, `eased`, `now`, `tick` are all
    // declared inside this effect — they cannot be in the dep array. `value`
    // and `inView` ARE present.
    useEffect(() => {
        if (!inView) return;
        const duration = 1800;
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setN(Math.round(eased * value));
            if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [inView, value]);

    return (
        <span
            ref={ref}
            className="font-accent text-4xl md:text-5xl text-[#D4AF37] leading-none"
        >
            {n.toLocaleString()}
            {suffix}
        </span>
    );
};

export const Impact = () => {
    const [stats, setStats] = useState(STATS_FALLBACK);
    const [activeId, setActiveId] = useState(INTERVIEWS[0].id);
    const active =
        INTERVIEWS.find((v) => v.id === activeId) ?? INTERVIEWS[0];

    // One-shot fetch on mount. `getStats` is a module-level import (stable),
    // `r` is a Promise callback parameter, `setStats` is a stable React
    // setter — none need to be in deps.
    useEffect(() => {
        getStats()
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, []);

    const selectVideo = (id) => {
        setActiveId(id);
    };

    const items = [
        { label: "Young Moguls Served", value: stats.young_moguls, suffix: "+" },
        { label: "Students Reached", value: stats.students_reached, suffix: "+" },
        { label: "School Districts", value: stats.school_districts, suffix: "" },
        { label: "Live Audience Reach", value: stats.live_audience, suffix: "+" },
        { label: "Certifications Earned", value: stats.certifications, suffix: "+" },
        { label: "Community Events", value: stats.community_events, suffix: "+" },
    ];

    return (
        <section
            id="impact"
            data-testid="impact-section"
            className="relative py-12 md:py-16 bg-[#050505] overflow-hidden"
        >
            {/* Big background number */}
            <div className="absolute -top-10 -right-10 font-display text-[18rem] md:text-[26rem] text-white/[0.02] leading-none select-none pointer-events-none">
                400+
            </div>

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative">
                {/* ============ STATS BLOCK (Community Impact) ============ */}
                <div className="grid lg:grid-cols-12 gap-12 mb-24">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 06 — Community Impact
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            We Don't Just
                            <br />
                            Inspire Creativity —
                            <br />
                            <span className="gold-text-gradient">
                                We Build Career Pathways.
                            </span>
                        </h2>
                        <p className="mt-8 text-white/65 leading-relaxed max-w-md">
                            Sacramento-rooted. Regionally scaled. The numbers
                            are real — and they grow with every cohort, every
                            stage, every district, and every industry
                            professional that joins the ecosystem.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 grid-rows-3 gap-px bg-[#D4AF37]/20 border border-[#D4AF37]/20 h-full">
                            {items.map((it, i) => (
                                <motion.div
                                    key={it.label}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.08 }}
                                    className="bg-[#0A0A0A] p-5 md:p-6 relative group hover:bg-[#111] transition-colors"
                                    data-testid={`impact-stat-${i}`}
                                >
                                    <Counter
                                        value={it.value}
                                        suffix={it.suffix}
                                    />
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mt-3">
                                        {it.label}
                                    </div>
                                    <div className="absolute top-3 right-3 text-[10px] font-mono text-[#D4AF37]/30">
                                        0{i + 1}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ============ EVERYDAY CREATIVE BLOCK ============ */}
                <div id="everyday-creative" className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-24 scroll-mt-24">
                    {/* LEFT — copy + CLTRE×EP graphic */}
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 07 — Everyday Creative
                        </span>
                        <h3 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1]">
                            The Everyday
                            <br />
                            <span className="gold-text-gradient">
                                Creative Internship
                            </span>
                        </h3>

                        <p className="mt-6 text-white/70 leading-relaxed">
                            A paid, year-round summer internship pipeline that
                            hires Young Mogul graduates as working creatives.
                            Interns produce real campaigns, run real shoots,
                            and sit in real rooms — turning the 9-week
                            accelerator into a long-form career pathway. From
                            studio sessions and brand drops to podcast
                            productions and on-set roles, the Everyday
                            Creative Internship Program by CLTRE and the
                            EDUtainment Pros, sponsored by Golden 1 Credit
                            Union, converts Young Moguls into creative
                            professionals.
                        </p>

                        <ul className="mt-6 space-y-2 text-sm text-white/60">
                            <li className="flex items-center gap-2">
                                <Briefcase
                                    size={14}
                                    className="text-[#D4AF37]"
                                />
                                Paid creative roles — content, audio,
                                production, design
                            </li>
                            <li className="flex items-center gap-2">
                                <Briefcase
                                    size={14}
                                    className="text-[#D4AF37]"
                                />
                                Direct mentorship from CLTRE + EP operators
                            </li>
                            <li className="flex items-center gap-2">
                                <Briefcase
                                    size={14}
                                    className="text-[#D4AF37]"
                                />
                                Portfolio-grade work presented to real
                                audiences
                            </li>
                        </ul>
                    </div>

                    {/* RIGHT — Interviews big-screen + thumbnails */}
                    <div
                        id="impact-interviews"
                        className="lg:col-span-7 scroll-mt-24"
                    >
                        <div className="flex items-end justify-between gap-4 mb-3">
                            <span className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] font-bold">
                                CLTRE × EP Presents
                            </span>
                            <a
                                href={`https://www.youtube.com/playlist?list=${PLAYLIST_ID}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-[#D4AF37] transition-colors"
                            >
                                Full Series
                                <ExternalLink size={11} />
                            </a>
                        </div>

                        <div className="relative aspect-video w-full overflow-hidden border border-[#D4AF37]/30 bg-black mb-3">
                            <LazyYouTube
                                videoId={activeId}
                                playlistId={PLAYLIST_ID}
                                title={active.title}
                                testId="interviews-featured"
                            />
                        </div>

                        <div className="flex items-center justify-between mb-4 text-[11px] uppercase tracking-[0.25em] text-white/55">
                            <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                                <span className="text-[#D4AF37] font-bold">
                                    Now Streaming
                                </span>
                                <span className="text-white/80 normal-case text-xs font-display uppercase tracking-tight">
                                    "{active.title}"
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3">
                            {INTERVIEWS.map((v, i) => {
                                const isActive = v.id === activeId;
                                return (
                                    <motion.button
                                        key={v.id}
                                        whileHover={{ y: -2 }}
                                        onClick={() => selectVideo(v.id)}
                                        data-testid={`interview-thumb-${i}`}
                                        aria-label={`Play ${v.title}`}
                                        className={`relative aspect-video overflow-hidden text-left group transition-all duration-300 ${
                                            isActive
                                                ? "border-2 border-[#D4AF37] gold-glow"
                                                : "border border-white/10 hover:border-[#D4AF37]/60"
                                        }`}
                                    >
                                        <img
                                            src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                                            alt={v.title}
                                            loading="lazy"
                                            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                                                isActive
                                                    ? ""
                                                    : "grayscale-[60%] group-hover:grayscale-0"
                                            }`}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                                        {!isActive && (
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="h-8 w-8 rounded-full bg-[#D4AF37] grid place-items-center">
                                                    <Play
                                                        size={11}
                                                        className="text-black ml-0.5"
                                                    />
                                                </span>
                                            </div>
                                        )}

                                        {isActive && (
                                            <div className="absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 bg-[#D4AF37] text-black text-[8px] uppercase tracking-[0.2em] font-bold">
                                                On
                                            </div>
                                        )}

                                        <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
                                            <div className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37] line-clamp-1">
                                                {v.guest}
                                            </div>
                                            <div className="font-display text-[11px] uppercase tracking-tight text-white mt-0.5 leading-tight line-clamp-1">
                                                {v.title}
                                            </div>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Sponsor marquee */}
                <div className="mt-20 pt-10 border-t border-white/10">
                    <div className="text-[11px] uppercase tracking-[0.4em] text-white/40 mb-6 text-center">
                        Powered By Community Partners
                    </div>
                    <div className="overflow-hidden no-scrollbar">
                        <motion.div
                            className="flex items-center whitespace-nowrap"
                            animate={{ x: ["0%", "-50%"] }}
                            transition={{
                                duration: 60,
                                ease: "linear",
                                repeat: Infinity,
                            }}
                        >
                            {[...PARTNERS, ...PARTNERS].map((p, i) => (
                                <span
                                    key={`${p}-${i}`}
                                    className="flex items-center shrink-0"
                                >
                                    <span className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white/40 hover:text-[#D4AF37] transition-colors px-8">
                                        {p}
                                    </span>
                                    <span className="text-[#D4AF37] text-xl">
                                        ✦
                                    </span>
                                </span>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
